import { z } from 'zod';
export const TerritoryRatingsSchema = z
    .object({
    strategicAlignment: z.number().min(0).max(5).describe('Rating for strategic alignment (0-5)'),
    breakthroughPotential: z
        .number()
        .min(0)
        .max(5)
        .describe('Rating for breakthrough potential (0-5)'),
    inspirationalPotential: z
        .number()
        .min(0)
        .max(5)
        .describe('Rating for inspirational potential (0-5)'),
})
    .describe('The 0-5 scores for the three specific rating categories shown on the card.');
export const TerritoryCoreTensionItemSchema = z
    .object({
    title: z
        .enum([
        'Category vs Consumer (Opportunity)',
        'Consumer vs Brand (Relevance)',
        'Brand vs Category (Advantage)',
        'Cultural Relevance',
    ])
        .describe('The specific core tension dimension.'),
    description: z
        .string()
        .describe('Write a single sentence describing the tension and why it matters.'),
})
    .describe('Core tension item capturing the key strategic tension and its explanation.');
export const TerritoryStrategicBriefItemSchema = z
    .object({
    title: z
        .enum(['GET', 'WHO', 'TO', 'BY'])
        .describe('Framework element name (Get / Who / To / By).'),
    description: z.string().describe('Write one concise line for this element.'),
})
    .describe('Element of the GET/WHO/TO/BY strategic framework section.');
export const TerritoryPillarItemSchema = z
    .object({
    title: z.string().describe('Pillar title supporting the key message.'),
    description: z
        .string()
        .describe('Brief explanation of what this pillar represents within the strategic plan.'),
})
    .describe('Strategic pillar element with title and description.');
export const TerritoryValidationItemSchema = z
    .object({
    title: z.enum(['KPIs', 'Engagement', 'Impact']).describe('Validation element name.'),
    description: z.string().describe('How this element will be measured and validated.'),
})
    .describe('Validation & Measurement item.');
export const TerritorySchema = z.object({
    id: z.number().describe('A unique integer identifier for the strategic territory.'),
    isRecommended: z.boolean().describe("True if 'Recommended' badge should be visible."),
    title: z
        .string()
        .describe('A short, powerful, memorable phrase capturing the strategic opportunity.'),
    summary: z.string().describe('A concise explanation of the strategic idea and why it matters.'),
    ratings: TerritoryRatingsSchema,
    keyMessage: z.string().describe('A single inspiring line capturing the heart of the direction.'),
    coreTensions: z
        .array(TerritoryCoreTensionItemSchema)
        .length(4)
        .describe('Exactly four core tensions.'),
    strategicBrief: z
        .array(TerritoryStrategicBriefItemSchema)
        .length(4)
        .describe('The GET/WHO/TO/BY framework with exactly four items.'),
    pillars: z
        .array(TerritoryPillarItemSchema)
        .min(3)
        .max(4)
        .describe('3-4 strategic pillars supporting the key message.'),
    audience: z
        .object({
        portrait: z.string().describe('Bring the target audience to life in a sharp, human way.'),
    })
        .describe('The Audience Portrait section.'),
    validation: z
        .array(TerritoryValidationItemSchema)
        .length(3)
        .describe('Exactly three validation items: KPIs, Engagement, Impact.'),
});
export const StrategicTerritoriesWidgetKey = 'strategic-territories:v1';
export const StrategicTerritoriesWidgetKind = 'strategy';
export const SAVED_STRATEGIC_TERRITORIES_ARTIFACT_KEY_PREFIX = 'saved-strategic-territories:';
/**
 * Prefix of the raw session widget keys (`strategic-territories:v1`, future
 * `:v2`, …) — the Strategy agent's live working artifact. Listing surfaces
 * (Library grid/count, "Add existing artifacts" picker) exclude this prefix so
 * only the saved single-territory artifacts above appear. Never a prefix of
 * `SAVED_STRATEGIC_TERRITORIES_ARTIFACT_KEY_PREFIX` keys (`saved-` first).
 */
export const STRATEGIC_TERRITORIES_ARTIFACT_KEY_PREFIX = 'strategic-territories:';
export const StrategicTerritoriesWidgetSchema = z.object({
    title: z
        .string()
        .nullable()
        .describe('The overall strategy title for the strategic territories.'),
    items: z
        .array(TerritorySchema)
        .describe('List of strategic territories, each providing a unique strategic direction.'),
});
